import { useId, useState, type ReactNode } from 'react';
import { SquarePenIcon } from '../../../icons/square-pen';
import { Trash2Icon } from '../../../icons/trash-2';
import { cn } from '../../../lib/utils';
import { DisabledReasonTooltip } from '../tooltip/disabled-reason-tooltip';

/**
 * Status line under the row content. `pending` paints it with the same
 * destructive tone as the "Com pendências" label of `ResumeSectionItem`.
 */
export interface ListItemStatus {
  /** Status text (e.g. "Com pendências"). */
  label: string;
  tone: 'pending';
}

export interface ListItemProps {
  /** Row title. Wraps on long words instead of overflowing. */
  title: ReactNode;
  /**
   * Secondary lines rendered under the title (e.g. type, institution, date).
   * Each line is clamped to a single line with an ellipsis unless `detailsWrap`.
   */
  details?: ReactNode[];
  /** Let detail lines wrap instead of truncating. */
  detailsWrap?: boolean;
  /**
   * Optional free-text paragraph. Past `descriptionMaxChars` it is cut with an
   * ellipsis and an inline "Mais" toggle expands it ("Menos" collapses).
   */
  description?: string;
  /** @default 120 */
  descriptionMaxChars?: number;
  /**
   * Optional status line rendered under the title/details (12px medium). Use
   * `{ label: 'Com pendências', tone: 'pending' }` to flag an item with
   * required fields missing. Read by screen readers in order with the title.
   */
  status?: ListItemStatus;
  /** Shows the edit icon button when provided. */
  onEdit?: () => void;
  /** Shows the delete icon button when provided. */
  onDelete?: () => void;
  /** Accessible name of the edit button. @default 'Editar' */
  editLabel?: string;
  /** Accessible name of the delete button. @default 'Excluir' */
  deleteLabel?: string;
  deleteDisabled?: boolean;
  /**
   * Why delete is disabled. With `deleteDisabled`, the button becomes
   * "soft-disabled" (`aria-disabled`, same visual as disabled): it stays
   * focusable/clickable, never calls `onDelete`, and shows this reason in a
   * tooltip on tap/click/focus (touch has no `title`). The reason is also
   * announced through `aria-describedby` (sr-only span).
   */
  deleteDisabledReason?: string;
  /** @default 'Mais' */
  moreLabel?: string;
  /** @default 'Menos' */
  lessLabel?: string;
  className?: string;
  /**
   * Root testid. Derived: edit button → `<testid>-edit`, delete button →
   * `<testid>-delete`, description toggle → `<testid>-more`.
   */
  'data-testid'?: string;
}

// 44×44 touch target around a 16px icon. The vertical negative margin cancels
// the extra height entirely (the row stays 16px tall, as in Figma); horizontally
// only 8px per side is cancelled so the two neighbouring targets never cover
// each other's icon (Figma draws a 6px gap between bare 16px icons — a full
// `-m-3.5` would let the delete target swallow half of the edit icon).
const actionButtonClass = cn(
  'inline-flex size-11 -my-3.5 -mx-2 shrink-0 items-center justify-center rounded-md text-primary transition-colors',
  'hover:text-primary/80',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-primary',
  'aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:text-primary',
  '[&_svg]:size-4 [&_svg]:shrink-0'
);

function ListItem({
  title,
  details,
  detailsWrap = false,
  description,
  descriptionMaxChars = 120,
  status,
  onEdit,
  onDelete,
  editLabel = 'Editar',
  deleteLabel = 'Excluir',
  deleteDisabled = false,
  deleteDisabledReason,
  moreLabel = 'Mais',
  lessLabel = 'Menos',
  className,
  'data-testid': testId,
}: ListItemProps) {
  const [expanded, setExpanded] = useState(false);
  const reasonId = useId();

  const hasActions = Boolean(onEdit || onDelete);
  const hasDetails = details != null && details.length > 0;
  const hasDescription = description != null && description !== '';
  const isLong = hasDescription && description.length > descriptionMaxChars;
  const shownDescription =
    isLong && !expanded ? `${description.slice(0, descriptionMaxChars).trimEnd()}…` : description;
  const deleteReasonId = deleteDisabled && deleteDisabledReason ? reasonId : undefined;

  return (
    <div
      data-slot="list-item"
      data-testid={testId}
      className={cn('flex w-full flex-col items-start gap-3', className)}
    >
      <div data-slot="list-item-header" className="flex w-full items-center gap-3">
        <span
          data-slot="list-item-title"
          className="min-w-0 flex-1 break-words text-sm font-medium leading-none text-foreground"
        >
          {title}
        </span>
        {hasActions && (
          <div
            data-slot="list-item-actions"
            // Pulls the group back by the 6px of target that sits outside the
            // icon on each side so the icons line up with the row edges.
            className="-mx-1.5 flex shrink-0 items-center gap-1.5"
          >
            {onEdit && (
              <button
                type="button"
                aria-label={editLabel}
                onClick={onEdit}
                data-testid={testId ? `${testId}-edit` : undefined}
                className={actionButtonClass}
              >
                <SquarePenIcon />
              </button>
            )}
            {onDelete &&
              (deleteReasonId ? (
                <>
                  <DisabledReasonTooltip reason={deleteDisabledReason as string}>
                    <button
                      type="button"
                      aria-label={deleteLabel}
                      aria-describedby={deleteReasonId}
                      data-testid={testId ? `${testId}-delete` : undefined}
                      className={actionButtonClass}
                    >
                      <Trash2Icon />
                    </button>
                  </DisabledReasonTooltip>
                  <span id={deleteReasonId} className="sr-only">
                    {deleteDisabledReason}
                  </span>
                </>
              ) : (
                <button
                  type="button"
                  aria-label={deleteLabel}
                  onClick={onDelete}
                  disabled={deleteDisabled}
                  data-testid={testId ? `${testId}-delete` : undefined}
                  className={actionButtonClass}
                >
                  <Trash2Icon />
                </button>
              ))}
          </div>
        )}
      </div>
      {hasDetails && (
        <div data-slot="list-item-details" className="flex w-full min-w-0 flex-col gap-1.5">
          {details.map((line, index) => (
            <span
              key={index}
              className={cn(
                'text-xs leading-none text-muted-foreground',
                detailsWrap ? 'break-words' : 'truncate'
              )}
            >
              {line}
            </span>
          ))}
        </div>
      )}
      {status && (
        <span
          data-slot="list-item-status"
          data-tone={status.tone}
          className="text-xs font-medium leading-none text-tone-red"
        >
          {status.label}
        </span>
      )}
      {hasDescription && (
        <p
          data-slot="list-item-description"
          className="w-full min-w-0 text-xs leading-4 tracking-[0.12px] text-muted-foreground"
        >
          {shownDescription}
          {isLong && (
            <>
              {' '}
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((prev) => !prev)}
                data-testid={testId ? `${testId}-more` : undefined}
                className="-mx-2 -my-3.5 px-2 py-3.5 text-xs font-medium leading-4 tracking-[0.12px] text-primary hover:underline focus-visible:underline focus-visible:outline-none"
              >
                {expanded ? lessLabel : moreLabel}
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}
ListItem.displayName = 'ListItem';

export { ListItem };
